import { useMemo } from 'react'
import { useFormContext, Controller, useForm } from 'react-hook-form'
import { Alert, DatePicker, SlimButton, OptionProps } from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import moment from 'moment'

import * as S from './styles'

type CustomPickerModalProps = {
  fieldName: string
  open: boolean
  previousValue: string
  setCustomInterval: (option: OptionProps) => void
}

// TODO: Replace momentjs with dayjs

const CustomPickerModal = ({
  fieldName,
  open,
  previousValue,
  setCustomInterval
}: CustomPickerModalProps) => {
  const { setValue } = useFormContext()

  const handleClose = () => {
    setValue(fieldName, previousValue)
    reset(defaultValues)
  }

  const defaultValues = {
    beginDate: moment().subtract(6, 'months').format('YYYY-MM'),
    endDate: moment().format('YYYY-MM')
  }

  const { control, trigger, handleSubmit, getValues, reset, formState } = useForm({
    defaultValues,
    mode: 'onChange'
  })

  const submitForm = handleSubmit((data) => {
    const startDate = moment(data.beginDate, 'YYYY-MM').startOf('month')
    const endDate = moment(data.endDate, 'YYYY-MM').endOf('day')

    if (startDate.format('MM/YYYY') === endDate.format('MM/YYYY')) {
      setCustomInterval({
        title: `${startDate.format('MM/YY')}`,
        value: `${startDate.format('L')} - ${endDate.format('L')}`
      })
    } else {
      setCustomInterval({
        title: `${startDate.format('MM/YY')} a ${endDate.format('MM/YY')}`,
        value: `${startDate.format('L')} - ${endDate.format('L')}`
      })
    }

    setValue(fieldName, `${startDate.format('L')} - ${endDate.format('L')}`)
  })

  const isValidInterval = useMemo(() => {
    const { beginDate, endDate } = getValues()
    if (formState.errors.beginDate || formState.errors.endDate) return true
    return moment(endDate).diff(moment(beginDate), 'month') <= 12
  }, [formState])

  return (
    <>
      {open && (
        <ModalContentLayout
          closeAction={handleClose}
          title="Período personalizado"
          titleSize={22}
        >
          <S.Content>
            <span>Indique um período de até 1 ano nos campos abaixo.</span>

            <S.FieldsWrapper>
              <Controller
                name="beginDate"
                control={control}
                rules={{
                  validate: {
                    min: (date) =>
                      moment(date)
                        .endOf('month')
                        .isAfter(moment().subtract(5, 'year').startOf('month')) ||
                      'Data inválida.'
                  }
                }}
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    minDate={moment().subtract(5, 'year') as unknown as string}
                    maxDate={moment().add(2, 'year') as unknown as string}
                    label="Mês cota inicial"
                    onChange={(date) => {
                      field.onChange(moment(date).format('YYYY-MM'))
                      trigger()
                    }}
                    error={fieldState.error?.message}
                    errorState={!isValidInterval}
                    views={['year', 'month']}
                    inputFormat="MM/YYYY"
                    disableMaskedInput={false}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={control}
                rules={{
                  validate: {
                    min: (date) =>
                      moment(date)
                        .endOf('month')
                        .isAfter(moment().subtract(5, 'year').startOf('month')) ||
                      'Data inválida.',
                    afterBegin: (date) =>
                      moment(date)
                        .endOf('month')
                        .isAfter(moment(getValues('beginDate')).startOf('month')) ||
                      'Período inválido.'
                  }
                }}
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    minDate={moment().subtract(5, 'year') as unknown as string}
                    maxDate={moment().add(2, 'year') as unknown as string}
                    label="Mês cota final"
                    onChange={(date) => {
                      field.onChange(moment(date).format('YYYY-MM'))
                      trigger()
                    }}
                    error={fieldState.error?.message}
                    errorState={!isValidInterval}
                    views={['year', 'month']}
                    inputFormat="MM/YYYY"
                    disableMaskedInput={false}
                  />
                )}
              />
            </S.FieldsWrapper>

            {!isValidInterval && (
              <Alert fullWidth kind="error" title="O período deve ser inferior a 1 ano" />
            )}
          </S.Content>

          <S.Footer>
            <S.ButtonWrapper>
              <SlimButton content="Cancelar" variant="secondary" onClick={handleClose} />
            </S.ButtonWrapper>
            <S.ButtonWrapper>
              <SlimButton
                content="Ok"
                variant="primary"
                onClick={submitForm}
                disabled={!isValidInterval || !formState.isValid}
              />
            </S.ButtonWrapper>
          </S.Footer>
        </ModalContentLayout>
      )}
    </>
  )
}

export default CustomPickerModal
